import {
    AdvancedList,
    FieldDefinition,
    FieldDefinitionDataType,
    Variable
} from "@amzn/altar-sds-client";
import {
    Action,
    Condition,
    FieldConfiguration,
    Workflow
} from "@amzn/ask-legal-domain";
import {
    FormField,
    FormFieldProps,
    Header,
    Input,
    InputProps,
    SelectProps,
    SpaceBetween
} from "@amzn/awsui-components-react";
import * as React from "react";
import { StaticListSelect } from "../../../../common/StaticListSelect";
import { UIField } from "../../../../common/UIField";
import { EmailNotification } from "./EmailNotification";
import { buildBodyWithItemLink, buildStaticEmailAction, extractEmailIdsFromAddresses, stripItemLink } from "./utils/EmailWorkflowUtils";

export const ItemDateReached = (props: {
    advancedList: AdvancedList;
    variables: Variable[];
    fieldConfigurations?: FieldConfiguration.Record;
    containerId?: string;
    value?: Workflow.Payload;
    onUpdated?: (value: Workflow.Payload) => void;
}) => {
    const condition = props.value?.condition as Condition.ItemDateFieldValueReached;
    const [formFieldWorkflowName, setFormFieldWorkflowName] = React.useState<FormFieldProps>({});
    const [workflowName, setWorkflowName] = React.useState<string>(props.value?.name ?? "");

    const [formField_targetDate, setFormField_targetDate] = React.useState<FormFieldProps>({});
    const [selectedField, setSelectedField] = React.useState<FieldDefinition | undefined>(
        props.advancedList.fieldDefinitions?.find((field) => field.fieldKey === condition?.fieldKey) ??
            undefined
    );

    const [formField_offsetInDays, setFormField_offsetInDays] = React.useState<FormFieldProps>({});
    const [input_offsetInDays, setInput_offsetInDays] = React.useState<Pick<InputProps, "value">>({
        value: ((props.value?.condition as Condition.ItemDateFieldValueReached)?.offsetInDays || 0).toString()
    });

    const action = props.value?.action;
    const isPersonFieldAction = props.value?.actionType === Action.Type.EmailToPersonFieldValue;
    const [emailNotification, setEmailNotification] = React.useState<EmailNotification>({
        usePersonField: isPersonFieldAction,
        personFieldKey: isPersonFieldAction ? (action as Action.EmailToPersonFieldValue).personFieldKey : "",
        emailSubject: action?.subject || "",
        emailBody: stripItemLink(action?.body || ""),
        emailAddressList: !isPersonFieldAction &&
            Array.isArray((action as Action.EmailToStaticRecipients)?.emailAddressesList)
            ? extractEmailIdsFromAddresses(
                (action as Action.EmailToStaticRecipients).emailAddressesList
            )
            : [],
    });

    const fieldDefinitions: FieldDefinition[] = props.advancedList?.fieldDefinitions ?? [];

    React.useEffect(() => {
        let valid = true;
        if (!workflowName?.trim().length) {
            setFormFieldWorkflowName({
                errorText: "Workflow Name is required"
            });
            valid = false;
        } else {
            setFormFieldWorkflowName({
                errorText: ""
            });
        }

        if (!selectedField) {
            setFormField_targetDate({
                errorText: "Target date column is required"
            });
            valid = false;
        } else {
            setFormField_targetDate({
                errorText: ""
            });
        }

        if (!input_offsetInDays.value) {
            setFormField_offsetInDays({
                errorText: "Offset days from target date is required"
            });
            valid = false;
        } else {
            setFormField_offsetInDays({
                errorText: ""
            });
        }
        const usePersonField = emailNotification.usePersonField ?? true;
        const hasSubject = emailNotification?.emailSubject?.trim().length;
        const hasBody = emailNotification?.emailBody?.trim().length;
        const hasPerson = emailNotification?.personFieldKey?.trim().length;
        const hasAliases = emailNotification?.emailAddressList?.length;
        if (
            !hasSubject ||
            !hasBody ||
            (usePersonField && !hasPerson) ||
            (!usePersonField && !hasAliases)
        ) {
            valid = false;
        }

        if (!valid) {
            console.warn(`not a valid data`, workflowName, emailNotification);
            return;
        }

        const condition = new Condition.ItemDateFieldValueReached(
            selectedField.fieldKey,
            Number(input_offsetInDays.value)
        );
        let action: Action.EmailToPersonFieldValue | Action.EmailToStaticRecipients;
        let actionType: Action.Type;
        const emailBodyWithLink = buildBodyWithItemLink(
            emailNotification.emailBody,
            props.containerId
        );
        if (usePersonField) {
            action = new Action.EmailToPersonFieldValue(
                emailNotification.emailSubject.trim(),
                emailBodyWithLink.trim(),
                emailNotification.personFieldKey.trim()
            );
            actionType = Action.Type.EmailToPersonFieldValue;
        } else {
            const result = buildStaticEmailAction(
                emailNotification.emailSubject,
                emailBodyWithLink,
                emailNotification.emailAddressList
            );
            action = result.action;
            actionType = result.actionType;
        }
        const workflowPayload = Workflow.Payload.init({
            id: props.value?.id,
            name: workflowName.trim(),
            conditionType: Condition.Type.ItemDateFieldValueReached,
            condition,
            action,
            actionType,
            variables: props.variables,
            ref: props.value?.ref,
            disabled: props.value?.disabled
        });

        props.onUpdated?.(workflowPayload);
    }, [workflowName, selectedField, input_offsetInDays.value, emailNotification]);

    return (
        <SpaceBetween size={"s"}>
            <SpaceBetween direction="vertical" size="xxs">
                <Header headingTagOverride="h4">Workflow Name</Header>
                <FormField errorText={formFieldWorkflowName.errorText}>
                    <Input value={workflowName} onChange={(e) => setWorkflowName(e.detail.value)} />
                </FormField>
            </SpaceBetween>

            <SpaceBetween direction="vertical" size="xxs">
                <Header headingTagOverride="h4">Workflow Criteria</Header>
                <UIField.CustomField
                    name="Track which column as target date?"
                    description="Choose a date field"
                    errorText={formField_targetDate.errorText as string}
                    child={
                        <StaticListSelect.Single<FieldDefinition>
                            data={fieldDefinitions.filter((e) => e.dataType === FieldDefinitionDataType.date)}
                            selected={selectedField}
                            onSelectChange={(selectedFieldDefintion: FieldDefinition) => {
                                setSelectedField(selectedFieldDefintion);
                            }}
                            fromOption={(option: SelectProps.Option) => {
                                return fieldDefinitions.find(
                                    (field) => field.fieldKey === option.value!
                                )! as FieldDefinition;
                            }}
                            toOption={(fieldDefinition: FieldDefinition) => {
                                return {
                                    label: fieldDefinition?.displayName!,
                                    value: fieldDefinition?.fieldKey!
                                };
                            }}
                        />
                    }
                />
                <UIField.CustomField
                    name="How many days before or after above target date?"
                    description="Input a positive or negative integer number"
                    errorText={formField_offsetInDays.errorText as string}
                    child={
                        <Input
                            {...input_offsetInDays}
                            type="number"
                            onChange={(e) => {
                                if (!e.detail.value) {
                                    setFormField_offsetInDays({
                                        errorText: "Offset in days is required"
                                    });
                                    return;
                                }
                                setInput_offsetInDays((prev) => ({
                                    ...prev,
                                    value: e.detail.value
                                }));
                            }}
                        />
                    }
                />
            </SpaceBetween>

            <EmailNotification
                fieldDefinitions={props.advancedList.fieldDefinitions ?? []}
                emailNotification={emailNotification}
                fieldConfigurations={props.fieldConfigurations}
                workflowNotificationType={Condition.Type.ItemDateFieldValueReached}
                containerId={props.containerId}
                onChanged={(value: EmailNotification) => {
                    setEmailNotification(value);
                }}
                variables={props.value?.variables || props.variables}
            />
        </SpaceBetween>
    );
};
